import React from 'react';
import PropTypes from 'prop-types';
import { NavigationVariants } from '../../libs/animations';
import Button from '../Button/Button';
import TruncateButton from '../Button/TruncateButton';
import {
  HeaderActionsLeft,
  HeaderActionsRight,
  Header,
  HeaderTitle,
  HeaderTitleAnimation,
  HeaderContent
} from './PageHeader.style';
import { setScrollPositionAppContainer } from '../../libs/fixDefaultScrollposition';
import debounce from '../../libs/debounce';
import NavigationHistory from '../../libs/NavigationHistory';
import {
  capture,
  PH_BACK_BUTTON_CLICKED,
  PH_NOTIFICATION_CENTER_CLICKED
} from '../../libs/posthogAnalytics';
/* Add widget names here to be picked up for translation */
i18nDefine(['Roster', 'Events', 'Timesheets', 'Timesheet']);

class WidgetPageHeader extends React.Component {
  static getDerivedStateFromProps(props) {
    return {
      headerTitle: WidgetPageHeader.getAppName(
        props.headerTitle,
        props.location,
        props.navs,
        props.linkUrlFor
      ),
      notificationsVersionStamp:
        props.rosternotifications.versionStamp +
        props.updatenotifications.versionStamp +
        props.timesheetnotifications.versionStamp
    };
  }

  static getAppName(headerTitle, location, navs, linkUrlFor) {
    return (
      headerTitle ||
      WidgetPageHeader.getAppNameForPath(location.pathname, navs, linkUrlFor)
    );
  }

  static getAppNameForPath(linkPath, navs, linkUrlFor) {
    const navigationItem = navs.find((nav) =>
      linkPath.startsWith(linkUrlFor(nav.linkTo))
    );
    return navigationItem ? i18n(navigationItem.nameMessage) : '';
  }

  constructor(props) {
    super(props);
    this.state = {
      headerTitle: WidgetPageHeader.getAppName(
        props.headerTitle,
        props.location,
        props.navs,
        props.linkUrlFor
      ),
      notificationsVersionStamp:
        props.rosternotifications.versionStamp +
        props.updatenotifications.versionStamp +
        props.timesheetnotifications.versionStamp
    };

    this.headerDomRef = React.createRef();

    this.resetScrollPosition = this.resetScrollPosition.bind(this);
    this.handleNotificationIconClick = debounce(
      this.handleNotificationIconClick.bind(this)
    );
    this.handleGoHomeOrBackButtonClick = debounce(
      this.handleGoHomeOrBackButtonClick.bind(this)
    );
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const { customRendererHeaderTitle, linkUrlFor } = this.props;
    return (
      WidgetPageHeader.getAppNameForPath(
        nextProps.location.pathname,
        nextProps.navs,
        linkUrlFor
      ) !== this.state.headerTitle ||
      customRendererHeaderTitle !== nextProps.customRendererHeaderTitle ||
      this.state.notificationsVersionStamp !== nextState.notificationsVersionStamp
    );
  }

  getHeaderHeight() {
    if (this.headerDomRef.current) return this.headerDomRef.current.clientHeight;
    return 0;
  }

  handleGoHomeOrBackButtonClick(event) {
    const { navigationHistory } = this.props;
    event.preventDefault();
    this.resetScrollPosition(event);
    capture(PH_BACK_BUTTON_CLICKED, { textBackButton: navigationHistory.getTextForBackButton() });
    navigationHistory.goBack();
  }

  handleNotificationIconClick() {
    const { navigationHistory, linkUrlFor } = this.props;
    this.resetScrollPosition();
    capture(PH_NOTIFICATION_CENTER_CLICKED);
    navigationHistory.goTo(linkUrlFor('/notifications'));
  }

  resetScrollPosition() {
    setScrollPositionAppContainer(0);
  }

  renderAppTitle() {
    const { customRendererHeaderTitle } = this.props;
    const { headerTitle } = this.state;
    if (customRendererHeaderTitle) return customRendererHeaderTitle();
    return headerTitle;
  }

  renderAppHeader() {
    const { navigationHistory, onClickHeaderTitle, showNotifications } = this.props;
    const { headerTitle } = this.state;
    const previousWidgetName = navigationHistory.getTextForBackButton();

    const onClickProp = {};
    if (onClickHeaderTitle) onClickProp.onClick = onClickHeaderTitle;
    return (
      <Header $fixed>
        <HeaderContent ref={this.headerDomRef}>
          <HeaderActionsLeft $paddingLeft={4}>
            <TruncateButton
              contentType="textWithIcon"
              color="primary"
              size="small"
              icon="navigate-before"
              iconPosition="left"
              onClick={this.handleGoHomeOrBackButtonClick}
              data-test-widget-header-back-button
            >
              {previousWidgetName}
            </TruncateButton>
          </HeaderActionsLeft>
          <HeaderTitle {...onClickProp} data-test-container="widgetTitle">
            <HeaderTitleAnimation
              key={`header${headerTitle}`}
              variants={NavigationVariants}
              initial="invisible"
              animate="visible"
              exit="invisible"
            >
              {this.renderAppTitle()}
            </HeaderTitleAnimation>
          </HeaderTitle>
          {showNotifications && (
            <HeaderActionsRight $paddingRight={12}>
              {this.renderNotificationsButton()}
            </HeaderActionsRight>
          )}
        </HeaderContent>
      </Header>
    );
  }

  renderNotificationsButton() {
    const { rosternotifications, updatenotifications, timesheetnotifications } = this.props;
    const hasUnreadNotifications =
      rosternotifications.hasUnreadNotifications() ||
      timesheetnotifications.hasUnreadNotifications() ||
      updatenotifications.hasUnreadNotifications();
    return (
      <Button
        contentType="icon"
        icon={hasUnreadNotifications ? 'notification' : 'notification-empty'}
        size="medium"
        color={hasUnreadNotifications ? 'primary' : 'default'}
        onClick={this.handleNotificationIconClick}
        dataAttributes={{
          'data-test-button': 'notifications'
        }}
      />
    );
  }

  render() {
    return <div>{this.renderAppHeader()}</div>;
  }
}

WidgetPageHeader.propTypes = {
  navs: PropTypes.arrayOf(
    PropTypes.shape({
      nameMessage: PropTypes.string,
      linkTo: PropTypes.string
    })
  ),
  rosternotifications: PropTypes.shape({
    versionStamp: PropTypes.number,
    hasUnreadNotifications: PropTypes.func
  }).isRequired,
  timesheetnotifications: PropTypes.shape({
    versionStamp: PropTypes.number,
    hasUnreadNotifications: PropTypes.func
  }).isRequired,
  updatenotifications: PropTypes.shape({
    versionStamp: PropTypes.number,
    hasUnreadNotifications: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired
  }).isRequired,
  history: PropTypes.shape({
    previousURL: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  }).isRequired,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  headerTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onClickHeaderTitle: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  showNotifications: PropTypes.bool,
  customRendererHeaderTitle: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  linkUrlFor: PropTypes.func.isRequired
};

WidgetPageHeader.defaultProps = {
  navs: [],
  headerTitle: false,
  showNotifications: true,
  customRendererHeaderTitle: false,
  onClickHeaderTitle: false
};

function ForwardRef(props, ref) {
  return <WidgetPageHeader {...props} ref={ref} />;
}

export default React.forwardRef(ForwardRef);
